import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown, Loader2 } from 'lucide-react';

/**
 * Async variant of SearchableSelect — fetches options from the server
 * via an `onSearch` callback instead of filtering a static array.
 */
const AsyncSearchableSelect = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyOption = null,
  selectedLabel = null,
  disabled = false,
  required = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Stores the label of the currently selected option so we can display it
  // even when the dropdown is closed and options are cleared.
  const [storedLabel, setStoredLabel] = useState(selectedLabel || null);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const searchIdRef = useRef(0);

  // Keep storedLabel in sync with selectedLabel prop changes
  useEffect(() => {
    if (selectedLabel != null) {
      setStoredLabel(selectedLabel);
    }
  }, [selectedLabel]);

  // Clear stored label when value is cleared externally
  useEffect(() => {
    if (!value) {
      setStoredLabel(null);
    }
  }, [value]);

  // Perform search via the onSearch callback
  const doSearch = useCallback(async (query) => {
    if (!onSearch) return;
    const id = ++searchIdRef.current;
    setLoading(true);
    try {
      const results = await onSearch(query);
      // Only apply results if this is still the latest search
      if (id === searchIdRef.current) {
        setOptions(results || []);
      }
    } catch (err) {
      console.error('AsyncSearchableSelect search error:', err);
      if (id === searchIdRef.current) {
        setOptions([]);
      }
    } finally {
      if (id === searchIdRef.current) {
        setLoading(false);
      }
    }
  }, [onSearch]);

  // Load initial results when dropdown opens
  useEffect(() => {
    if (isOpen) {
      doSearch(search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Debounced search on keystroke
  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(search);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, isOpen, doSearch]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = useCallback((val, option) => {
    if (option) {
      setStoredLabel(option.label);
    }
    onChange(val, option || null);
    setIsOpen(false);
    setSearch('');
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    }
  }, []);

  // Determine display text for the trigger button
  const currentOption = options.find(o => String(o.value) === String(value));
  const displayText = currentOption?.label || storedLabel;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        className={`w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
        } ${!displayText ? 'text-gray-500' : 'text-gray-900'}`}
      >
        <span className="truncate">
          {displayText || (emptyOption?.label ?? emptyOption) || placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 flex flex-col"
          onKeyDown={handleKeyDown}
        >
          {/* Search input — always shown */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-yellow-500"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto flex-1">
            {/* Empty/default option */}
            {emptyOption && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 ${
                  !value ? 'bg-yellow-50 text-yellow-700' : 'text-gray-500'
                }`}
              >
                {emptyOption?.label ?? emptyOption}
              </button>
            )}

            {loading ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {search ? 'No matches found' : 'Type to search...'}
              </div>
            ) : (
              options.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value, option)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-b-0 ${
                    String(option.value) === String(value) ? 'bg-yellow-50 text-yellow-700 font-medium' : 'text-gray-900'
                  }`}
                >
                  <div className="truncate">{option.label}</div>
                  {option.sublabel && (
                    <div className="text-xs text-gray-400 truncate">{option.sublabel}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Hidden input for form validation */}
      {required && (
        <input
          type="text"
          value={value || ''}
          required
          tabIndex={-1}
          className="absolute opacity-0 h-0 w-0"
          onChange={() => {}}
        />
      )}
    </div>
  );
};

export default AsyncSearchableSelect;
